import React from "react"
import ReactApexChart from "react-apexcharts"
import moment from "moment"
import { CardBody } from "reactstrap"
import { getWeekTooltipLabel } from "utils/dateUtil"

const AreaChart = ({ category }) => {
  const sorted = category
    ? [...(category?.revenue || [])].sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      )
    : []

  const useWeeklyLabels = true

  // ✅ Always store actual dates for tooltip
  const actualDates = sorted.map(i => moment(i?.date).format("DD-MM-YYYY"))

  // ✅ X-axis: W-1, W-2... if > 10 points, else actual dates
  const categories = useWeeklyLabels
    ? sorted.map((_, i) => `${i + 1}`)
    : actualDates

  const values = sorted.map(i => Number(i?.totalWeeklySalesValue) || 0)

  const total = values.reduce((a, b) => a + b, 0)
  const avg = values.length ? (total / values.length).toFixed(0) : 0
  const max = values.length ? Math.max(...values) : 0
  const min = values.length ? Math.min(...values) : 0

  const options = {
    chart: {
      type: "area",
      toolbar: { show: false },
      background: "transparent",
      foreColor: "#1a1a2e",
    },
    colors: ["#4361ee"],
    stroke: {
      show: true,
      curve: "smooth",
      width: 2.5,
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.4,
        opacityTo: 0.02,
        stops: [0, 90, 100],
        colorStops: [
          { offset: 0, color: "#4361ee", opacity: 0.4 },
          { offset: 100, color: "#4361ee", opacity: 0.02 },
        ],
      },
    },
    markers: {
      size: 4,
      colors: ["#fff"],
      strokeColors: "#4361ee",
      strokeWidth: 2,
      hover: { size: 6 },
    },
    dataLabels: { enabled: false },
    xaxis: {
      categories,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        // ✅ No rotation needed for W-1, W-2 short labels
        rotate: useWeeklyLabels ? 0 : -45,
        style: { colors: "#1a1a2e", fontSize: "11px", fontWeight: 600 },
      },
    },
    yaxis: {
      title: {
        text: "Revenue (£)",
        style: { color: "#1a1a2e", fontSize: "12px", fontWeight: 700 },
      },
      labels: {
        style: { colors: "#1a1a2e", fontSize: "11px", fontWeight: 600 },
        formatter: val => `£ ${val.toLocaleString("en-GB")}`,
      },
    },
    grid: {
      borderColor: "#e8e8f0",
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
    },
    tooltip: {
      theme: "light",
      shared: true,
      intersect: false,
      // ✅ Show actual date on hover in weekly mode
      x: {
        formatter: (val, { dataPointIndex }) => {
          return getWeekTooltipLabel(
            dataPointIndex,
            actualDates[dataPointIndex]
          )
        },
      },
      y: { formatter: val => `£ ${Math.round(val).toLocaleString("en-GB")}` },
    },
    legend: { show: false },
  }

  return (
    <div className="mt-4 card bg-light rounded-4">
      <CardBody>
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-3 px-2">
          <div className="d-flex align-items-end gap-2">
            <div>
              <h5
                className="mb-1 fw-bold"
                style={{ color: "#1a1a2e", fontSize: "16px" }}
              >
                {category?.subCategoryName} — Revenue
              </h5>
              <small
                style={{ color: "#666", fontSize: "13px", fontWeight: 500 }}
              >
                {sorted.length > 0
                  ? `${moment(sorted[0]?.date).format("DD-MM-YYYY")} — ${moment(
                      sorted[sorted.length - 1]?.date
                    ).format("DD-MM-YYYY")}`
                  : ""}
              </small>
            </div>
            <h6 style={{ color: "#666", fontWeight: 500 }} className="mb-1">
              (Year To Date)
            </h6>
          </div>

          {/* Total badge */}
          <div
            style={{
              background: "#eef0fb",
              color: "#4361ee",
              borderRadius: "8px",
              padding: "4px 14px",
              fontSize: "14px",
              fontWeight: 600,
            }}
          >
            Total:{" "}
            <strong>£ {Math.round(total).toLocaleString("en-GB")}</strong>
          </div>
        </div>

        {/* Stats */}
        <div className="d-flex gap-3 mb-3 px-2">
          {[
            {
              label: "PEAK",
              value: `£ ${Math.round(max).toLocaleString("en-GB")}`,
              color: "#22c55e",
              bg: "#f0fdf4",
            },
            {
              label: "LOWEST",
              value: `£ ${Math.round(min).toLocaleString("en-GB")}`,
              color: "#ef4444",
              bg: "#fef2f2",
            },
            {
              label: "AVERAGE",
              value: `£ ${Math.round(avg).toLocaleString("en-GB")}`,
              color: "#4361ee",
              bg: "rgb(207, 235, 248)",
            },
            {
              label: "TOTAL",
              value: `£ ${Math.round(total).toLocaleString("en-GB")}`,
              color: "#f59e0b",
              bg: "#fffbeb",
            },
          ].map((stat, i) => (
            <div
              key={i}
              style={{
                flex: 1,
                background: stat.bg,
                borderRadius: "10px",
                padding: "10px 14px",
                borderLeft: `4px solid ${stat.color}`,
              }}
            >
              <div style={{ fontSize: "11px", color: "#333", fontWeight: 600 }}>
                {stat.label}
              </div>
              <div
                style={{ fontSize: "16px", fontWeight: 800, color: stat.color }}
              >
                {stat.value}
              </div>
            </div>
          ))}
        </div>

        {/* Chart */}
        <ReactApexChart
          options={options}
          series={[
            { name: `${category?.subCategoryName} Revenue`, data: values },
          ]}
          type="area"
          height={300}
          width="100%"
        />

        {/* Legend */}
        <div
          className="d-flex justify-content-center gap-4 mt-2"
          style={{ borderTop: "1px solid #f0f0f0", paddingTop: "10px" }}
        >
          <div className="d-flex align-items-center gap-1">
            <span
              style={{
                width: 16,
                height: 2,
                background: "#4361ee",
                display: "inline-block",
              }}
            />
            <small
              style={{ color: "#1a1a2e", fontWeight: 600, fontSize: "11px" }}
            >
              {category?.subCategoryName} Revenue
            </small>
          </div>
        </div>
      </CardBody>
    </div>
  )
}

export default AreaChart
